import { useCallback, useEffect, useRef, useState } from "react"
import { API_URL } from "../../data/constants/apiConstants"
import { STATUS } from "./deviceCheckRules"

/**
 * Mesure la connexion AVANT d'entrer dans la salle.
 *
 * L'indicateur de qualité de LiveKit ne sait rien dire ici : il lit les
 * statistiques du flux qu'on envoie, et tant qu'on n'est pas entré il n'y a
 * aucun flux. On mesure donc nous-mêmes le temps d'aller-retour jusqu'à notre
 * serveur, ce qui suffit à distinguer une connexion confortable d'une connexion
 * qui va faire saccader la séance.
 *
 * Limite assumée : c'est une mesure de latence, pas de débit. Un vrai test de
 * débit demanderait un fichier de test servi par le backend — à ouvrir en
 * issue séparée si l'on veut aller plus loin.
 */

const SAMPLES = 3
const GOOD_MS = 350 // en dessous, la séance est confortable, y compris en 4G
const POOR_MS = 900 // au-delà, la voix elle-même commence à hacher
const TIMEOUT_MS = 4000

/** Le médian plutôt que la moyenne : un pic isolé ne doit pas noircir le verdict. */
function median(values) {
    const sorted = [...values].sort((a, b) => a - b)
    return sorted[Math.floor(sorted.length / 2)]
}

export function networkStatus(latencyMs) {
    if (latencyMs === null || latencyMs === undefined) return STATUS.PENDING
    // Mesure impossible : on ne sait pas, donc on ne prétend pas savoir. Un
    // échec de sonde peut venir d'un blocage réseau local ou d'un serveur
    // injoignable, pas forcément d'une mauvaise connexion de l'élève.
    if (latencyMs < 0) return STATUS.WARN
    if (latencyMs <= GOOD_MS) return STATUS.OK
    if (latencyMs <= POOR_MS) return STATUS.WARN
    return STATUS.BAD
}

export function networkDetail(latencyMs) {
    const status = networkStatus(latencyMs)
    if (status === STATUS.PENDING) return "Test en cours…"
    if (status === STATUS.OK) return "Connexion rapide"
    if (latencyMs < 0) return "On n'a pas réussi à mesurer ta connexion"
    if (status === STATUS.WARN) return "Connexion un peu lente"
    return "Connexion très instable"
}

async function pingOnce() {
    const startedAt = Date.now()
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null
    const timer = controller ? setTimeout(() => controller.abort(), TIMEOUT_MS) : null

    try {
        // On sonde un FICHIER STATIQUE, servi directement par le serveur web
        // sans démarrer l'application. Mesuré en local : une route qui traverse
        // le framework coûte ~640 ms, le même aller-retour sur un fichier
        // statique en coûte ~22. La première version facturait donc le temps de
        // démarrage du backend à la connexion de l'élève, et affichait
        // « connexion très instable » sur une machine locale.
        //
        // `no-cors` parce qu'un fichier statique ne porte pas d'en-tête CORS :
        // la réponse est opaque, on ne peut pas la lire — et c'est sans
        // importance, seul le temps d'aller-retour nous intéresse.
        await fetch(`${API_URL}/favicon.ico?t=${startedAt}`, {
            method: "GET",
            mode: "no-cors",
            cache: "no-store",
            signal: controller?.signal,
        })
        return Date.now() - startedAt
    } catch (error) {
        return -1
    } finally {
        if (timer) clearTimeout(timer)
    }
}

export const useNetworkCheck = () => {
    const [latency, setLatency] = useState(null)
    const [isMeasuring, setIsMeasuring] = useState(false)
    const mounted = useRef(true)

    const measure = useCallback(async () => {
        setIsMeasuring(true)
        const samples = []
        for (let i = 0; i < SAMPLES; i += 1) {
            // Séquentiel et non parallèle : trois requêtes lancées ensemble se
            // gêneraient et gonfleraient artificiellement le temps mesuré.
            samples.push(await pingOnce()) // eslint-disable-line no-await-in-loop
        }

        const failed = samples.filter((value) => value < 0).length
        const next = failed >= SAMPLES ? -1 : median(samples.filter((value) => value >= 0))

        if (mounted.current) {
            setLatency(next)
            setIsMeasuring(false)
        }
        return next
    }, [])

    useEffect(() => {
        mounted.current = true
        measure()
        return () => {
            mounted.current = false
        }
    }, [measure])

    return { latency, status: networkStatus(latency), isMeasuring, measure }
}
